import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api/contexto'
import type { ErrorApi } from '../api/errores'
import type { DatosProducto, Producto, RespuestaAjustes } from '../api/modelos'

/** Catálogo interno (RF-41). `todos` incluye los desactivados. */
export function useCatalogo(todos = true) {
  const api = useApi()
  return useQuery<{ items: Producto[]; total: number }, ErrorApi>({
    queryKey: ['catalogo', todos],
    queryFn: ({ signal }) =>
      api.pedir('/api/productos', { parametros: todos ? { activos: 'false' } : undefined, senal: signal }),
  })
}

function useRefrescarCatalogo() {
  const qc = useQueryClient()
  return () => void qc.invalidateQueries({ queryKey: ['catalogo'] })
}

export function useCrearProducto() {
  const api = useApi()
  const refrescar = useRefrescarCatalogo()
  return useMutation<Producto, ErrorApi, DatosProducto>({
    mutationFn: (datos) => api.pedir<Producto>('/api/productos', { metodo: 'POST', cuerpo: datos }),
    onSuccess: refrescar,
  })
}

export function useEditarProducto() {
  const api = useApi()
  const refrescar = useRefrescarCatalogo()
  return useMutation<Producto, ErrorApi, { id: string; datos: DatosProducto }>({
    mutationFn: ({ id, datos }) => api.pedir<Producto>(`/api/productos/${id}`, { metodo: 'PATCH', cuerpo: datos }),
    onSuccess: refrescar,
  })
}

export function useCambiarActivoProducto() {
  const api = useApi()
  const refrescar = useRefrescarCatalogo()
  return useMutation<Producto, ErrorApi, { id: string; activo: boolean }>({
    mutationFn: ({ id, activo }) =>
      api.pedir<Producto>(activo ? `/api/productos/${id}/reactivar` : `/api/productos/${id}/desactivar`, { metodo: 'POST' }),
    onSuccess: refrescar,
  })
}

/** Ajustes (RF-47). También los lee el catálogo para el aviso de envase. */
export function useAjustes(activo = true) {
  const api = useApi()
  return useQuery<RespuestaAjustes, ErrorApi>({
    queryKey: ['ajustes'],
    queryFn: ({ signal }) => api.pedir<RespuestaAjustes>('/api/ajustes', { senal: signal }),
    enabled: activo,
  })
}

/**
 * Nombres que exige el resolvedor: cada bono lleva `<envase> <litros>` y
 * `<devolución> <litros>` (ajustes `pedido_producto_envase/_devolucion`).
 * Devuelve los que no tienen ningún producto activo que empiece por ellos.
 */
export function productosEnvaseQueFaltan(
  productos: Pick<Producto, 'nombre' | 'activo'>[],
  envase = 'ENVASE',
  devolucion = 'DEVOLUCION ENVASE',
): string[] {
  const activos = productos.filter((p) => p.activo).map((p) => p.nombre.trim().toUpperCase())
  return [envase, devolucion]
    .map((n) => n.trim())
    .filter((n) => n.length > 0)
    .filter((n) => !activos.some((nombre) => nombre === n.toUpperCase() || nombre.startsWith(`${n.toUpperCase()} `)))
}
